<script setup lang="ts">
/**
 * ProductQuotePanel — 서버 견적 요약(단품가 합계 · 할인액 · 최종 금액 분리 표시).
 * 모든 숫자는 OrderQuote 에서만 읽는다(클라 계산·추정 금지). 기본 포함 범위(표지 · Part 1)는 quote.includedScopes 를
 * 가격 없는 "기본 포함" 행으로 보인다. 상한 안내는 견적이 있으면 quote.notices, 없을 때만 그룹의 orderCap.notice(중복 표시 금지).
 * 견적이 없을 때(미선택·비로그인·조회 중·실패)는 금액 대신 상태 문구만 보인다.
 */
import { BaseButton } from '#/components/ui';
import { won } from '#/data/pricing';
import type { OrderQuote } from '#/types/catalog';

defineProps<{
  quote: OrderQuote | undefined;
  selectedCount: number;
  /** 'guest' 면 견적 대신 로그인 안내, 'loading'/'error' 는 상태 문구. */
  state: 'guest' | 'idle' | 'loading' | 'error' | 'ready';
  capNotice: string | null;
  submitLabel: string;
}>();

const emit = defineEmits<{
  submit: [];
  retry: [];
}>();
</script>

<template>
  <div class="rounded-card border border-line bg-white p-6 shadow-sm">
    <h2 class="mb-4 text-[15px] font-extrabold tracking-[-0.01em] text-brand">주문 요약</h2>

    <p v-if="state === 'idle'" class="text-[13px] leading-[1.6] text-muted">
      구매할 구성을 선택하면 최종 금액을 보여 드려요.
    </p>
    <p v-else-if="state === 'guest'" class="text-[13px] leading-[1.6] text-muted">
      로그인하면 선택한 구성의 최종 금액을 확인할 수 있어요.
    </p>
    <p v-else-if="state === 'loading'" class="text-[13px] text-muted" role="status">
      금액을 계산하는 중입니다…
    </p>
    <div v-else-if="state === 'error'" role="alert">
      <p class="text-[13px] leading-[1.6] text-danger">금액을 불러오지 못했습니다.</p>
      <BaseButton class="mt-3" variant="line" size="sm" @click="emit('retry')"
        >다시 시도</BaseButton
      >
    </div>

    <template v-else-if="quote">
      <ul class="flex flex-col gap-2">
        <li
          v-for="scope in quote.includedScopes"
          :key="`included-${scope}`"
          class="flex justify-between gap-3 text-[13.5px] text-body"
        >
          <span>{{ scope }}</span>
          <span class="font-bold text-accent-deep">기본 포함</span>
        </li>
        <li
          v-for="o in quote.selectedOptions"
          :key="o.id"
          class="flex justify-between gap-3 text-[13.5px] text-body"
        >
          <span>{{ o.name }}</span>
          <span>{{ won(o.listPrice) }}원</span>
        </li>
      </ul>
      <div class="my-3 border-t border-line" />
      <div class="flex justify-between text-[13.5px] text-body">
        <span>단품가 합계</span>
        <span>{{ won(quote.listSubtotal) }}원</span>
      </div>
      <div v-if="quote.discountAmount > 0" class="mt-2 flex justify-between text-[13.5px]">
        <span class="text-body">함께 구매 할인</span>
        <span class="font-bold text-accent-deep">-{{ won(quote.discountAmount) }}원</span>
      </div>
      <div class="mt-3 flex items-end justify-between gap-3 border-t border-line pt-3">
        <span class="text-[13.5px] font-bold text-brand">최종 결제 금액</span>
        <span class="font-serif text-[24px] font-extrabold tracking-[-0.02em] text-brand">
          {{ won(quote.amount) }}<span class="ml-1 text-[15px]">원</span>
        </span>
      </div>
      <ul v-if="quote.notices.length > 0" class="mt-3 flex flex-col gap-1">
        <li v-for="n in quote.notices" :key="n" class="text-[12px] leading-[1.5] text-muted">
          {{ n }}
        </li>
      </ul>
      <p v-if="!quote.canOrder && quote.blockingReason" class="mt-3 text-[12.5px] text-danger">
        {{ quote.blockingReason }}
      </p>
    </template>

    <!-- 견적이 있으면 같은 안내가 quote.notices 에 실린다 — 두 번 보이지 않게 견적 전 상태에서만 표시 -->
    <p
      v-if="capNotice && !(state === 'ready' && quote)"
      class="mt-4 text-[12px] leading-[1.6] text-muted"
    >
      {{ capNotice }}
    </p>

    <BaseButton
      class="mt-5"
      variant="accent"
      size="lg"
      block
      type="button"
      :disabled="
        selectedCount === 0 || (state === 'ready' && !quote?.canOrder) || state === 'loading'
      "
      @click="emit('submit')"
    >
      {{ submitLabel }}
    </BaseButton>
  </div>
</template>
